import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { TEAM_ROLES } from '../../../data/nurseAppModels';

const CreateShiftPlanPage = () => {
  const navigate = useNavigate();
  const { createPlan } = useShiftPlan();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Check if user has permission
  const canCreatePlan = currentTeam && (
    currentTeam.userRole === TEAM_ROLES.LEADER || 
    currentTeam.userRole === TEAM_ROLES.DEPUTY_LEADER
  );

  // Generate available months (current month + next 3 months)
  const availableMonths = useMemo(() => {
    const months = [];
    const today = new Date();
    
    for (let i = 0; i < 4; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
      
      // Thai month names
      const thaiMonths = [
        'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน',
        'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม',
        'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
      ];
      
      months.push({
        key: monthKey,
        year: year + 543, // Convert to Buddhist era
        month: month + 1,
        monthName: thaiMonths[month],
        displayName: `${thaiMonths[month]} ${year + 543}`,
        date: date
      });
    }
    
    return months;
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!selectedMonth) {
        throw new Error('กรุณาเลือกเดือนที่จะวางแผน');
      }

      // Create plan data
      const planData = {
        targetMonth: selectedMonth.key,
        monthsAhead: 1, // Default to 1 month ahead
        planNumber: null, // Will be auto-generated
        version: 1 // Will be auto-generated
      };

      const result = await createPlan(planData);

      if (result.success) {
        // Navigate back to shifts list
        // Members will receive notification and can submit their shifts
        navigate('/nurse/shifts');
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการสร้างแผน');
      }
    } catch (err) {
      setError(err.message || 'เกิดข้อผิดพลาดในการสร้างแผน');
    } finally {
      setLoading(false);
    }
  };

  // If no team selected
  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  // If no permission
  if (!canCreatePlan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์สร้างแผน</p>
          <p className="text-sm text-gray-500 mb-4">เฉพาะหัวหน้าทีมและรองหัวหน้าทีมเท่านั้น</p>
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าจัดการเวร
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับไปหน้าจัดการเวร
          </button>
          <h1 className="text-2xl font-bold text-gray-800">สร้างแผนเวรใหม่</h1>
          <p className="text-sm text-gray-600 mt-1">
            สร้างแผนเวรสำหรับทีม <span className="font-medium">{currentTeam.name}</span>
          </p>
        </div>

        {/* Team Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">ข้อมูลทีม</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">แผนก</p>
              <p className="font-medium text-gray-800">{currentTeam.department || 'ยังไม่ระบุ'}</p>
            </div>
            {currentTeam.hospital && (
              <div>
                <p className="text-sm text-gray-600">โรงพยาบาล</p>
                <p className="font-medium text-gray-800">{currentTeam.hospital}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">จำนวนสมาชิก</p>
              <p className="font-medium text-gray-800">{currentTeam.members?.length || 0} คน</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">หัวหน้าทีม</p>
              <p className="font-medium text-gray-800">{currentTeam.leaderName || 'ยังไม่ระบุ'}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Month Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              เลือกเดือนที่จะวางแผน *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {availableMonths.map((month) => (
                <button
                  key={month.key}
                  type="button"
                  onClick={() => setSelectedMonth(month)}
                  className={`
                    px-6 py-4 rounded-lg border-2 transition-all duration-200
                    ${selectedMonth?.key === month.key
                      ? 'border-blue-500 bg-blue-50 shadow-md transform scale-105'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                    }
                  `}
                >
                  <div className="text-center">
                    <div className={`
                      text-2xl mb-2
                      ${selectedMonth?.key === month.key ? 'text-blue-600' : 'text-gray-400'}
                    `}>
                      📅
                    </div>
                    <div className={`
                      font-bold text-lg
                      ${selectedMonth?.key === month.key ? 'text-blue-700' : 'text-gray-700'}
                    `}>
                      {month.monthName}
                    </div>
                    <div className={`
                      text-sm mt-1
                      ${selectedMonth?.key === month.key ? 'text-blue-600' : 'text-gray-500'}
                    `}>
                      {month.year}
                    </div>
                    {selectedMonth?.key === month.key && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          ✓ เลือกแล้ว
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            {!selectedMonth && (
              <p className="text-sm text-gray-500 mt-2">กรุณาเลือกเดือนที่จะวางแผน</p>
            )}
          </div>

          {/* Info Section */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">ℹ️ ข้อมูลเพิ่มเติม</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• เลขแผนและ Version จะถูกสร้างอัตโนมัติ</li>
              <li>• สามารถสร้าง Version ใหม่ได้เมื่อมีการปรับแผน</li>
              <li>• Version ล่าสุดจะเป็น Version ที่ Active</li>
              <li>• สมาชิกจะได้รับแจ้งเตือนเมื่อสร้างแผนเสร็จ</li>
            </ul>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/nurse/shifts')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading || !selectedMonth}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'กำลังสร้างแผน...' : 'สร้างแผน'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateShiftPlanPage;



